import { ActionIcon, Button, Group, Menu, Popover, Stack, Text, Textarea, Tooltip } from '@mantine/core'
import { getHotkeyHandler } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconDots, IconPencil, IconTrash } from '@tabler/icons-react'
import {
  type ActivityItem,
  type ActivityResponse,
  type FieldChange,
  formatDateTime,
  fromNow,
  type Locale,
  MAX_COMMENT_LENGTH,
  type RefMap,
  type Resource,
} from '@tnn/core'
import { InitialsAvatar, SectionCard, StatusPill } from '@tnn/ui'
import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useComments } from '../api/resources'
import { useSession } from '../auth/session'
import classes from './DocActivity.module.css'
import { fieldText } from './FieldValue'

const PAGE = 8

/** "สถานะ: ยังไม่ชำระ → ชำระแล้ว"; lists (items, payment lines) say only that they changed */
function changeText(resource: Resource, change: FieldChange, refs: RefMap, t: TFunction, locale: Locale) {
  const field = resource.field(change.field)
  const label = field ? t(field.label) : change.field
  if (!field || field.type === 'json') return `${label}: ${t('activity.listChanged')}`
  const ctx = { resource, refs, t, locale }
  const value = (v: unknown) => (v == null || v === '' ? '–' : fieldText(field, v, ctx) || String(v))
  return `${label}: ${value(change.from)} → ${value(change.to)}`
}

function When({ at, locale }: { at: string; locale: Locale }) {
  return (
    <Tooltip label={formatDateTime(at, { locale })}>
      <Text span size="xs" c="dimmed">
        {fromNow(at, { locale })}
      </Text>
    </Tooltip>
  )
}

function Comment({
  item,
  resource,
  recordId,
  mine,
  locale,
}: {
  item: ActivityItem
  resource: Resource
  recordId: string
  mine: boolean
  locale: Locale
}) {
  const { t } = useTranslation()
  const comments = useComments(resource, recordId)
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(item.body ?? '')
  const save = async () => {
    if (!text.trim()) return
    await comments.edit.mutateAsync({ commentId: item.id, body: text.trim() })
    setEditing(false)
  }
  const askDelete = () =>
    modals.openConfirmModal({
      title: t('activity.deleteTitle'),
      children: <Text size="sm">{t('activity.deleteBody')}</Text>,
      labels: { confirm: t('actions.delete'), cancel: t('actions.back') },
      confirmProps: { color: 'danger' },
      onConfirm: () => comments.remove.mutate(item.id),
    })
  return (
    <div className={classes.comment}>
      <Group gap={8} wrap="nowrap" justify="space-between">
        <Group gap={8} wrap="nowrap">
          <Text span size="sm" fw={500}>
            {item.userName}
          </Text>
          <When at={item.at} locale={locale} />
          {item.editedAt ? (
            <Text span size="xs" c="dimmed">
              · {t('activity.edited')}
            </Text>
          ) : null}
        </Group>
        {mine && !editing && (
          <Menu>
            <Menu.Target>
              <ActionIcon size={28} aria-label={t('actions.more')}>
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPencil size={16} />} onClick={() => setEditing(true)}>
                {t('actions.edit')}
              </Menu.Item>
              <Menu.Item color="danger" leftSection={<IconTrash size={16} />} onClick={askDelete}>
                {t('actions.delete')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
      {editing ? (
        <Stack gap={8} mt={6}>
          <Textarea
            autosize
            minRows={2}
            maxLength={MAX_COMMENT_LENGTH}
            value={text}
            aria-label={t('activity.comment')}
            onChange={(e) => setText(e.currentTarget.value)}
            onKeyDown={getHotkeyHandler([['mod+Enter', save]])}
          />
          <Group gap={8} justify="flex-end">
            <Button variant="default" size="compact-sm" onClick={() => setEditing(false)}>
              {t('actions.cancel')}
            </Button>
            <Button size="compact-sm" onClick={save} loading={comments.edit.isPending}>
              {t('actions.save')}
            </Button>
          </Group>
        </Stack>
      ) : (
        <Text size="sm" className={classes.body}>
          {item.body}
        </Text>
      )}
    </div>
  )
}

/**
 * Under a document: a box to comment, then everything that happened to it, newest first —
 * created, every field change (old → new), submitted, cancelled, paid — and the comments.
 */
export function DocActivity({
  resource,
  recordId,
  activity,
  locale,
}: {
  resource: Resource
  recordId: string
  activity?: ActivityResponse
  locale: Locale
}) {
  const { t } = useTranslation()
  const { me } = useSession()
  const comments = useComments(resource, recordId)
  const [text, setText] = useState('')
  const [all, setAll] = useState(false)
  const items = activity?.items ?? []
  const shown = all ? items : items.slice(0, PAGE)
  // "Edited after submit" is someone changing what was typed; the server moving a document on (received, approved) is not
  const input = new Set(Object.keys(resource.schema.shape))
  const editedAfter = (item: ActivityItem) => !!item.afterSubmit && (item.changes ?? []).some((c) => input.has(c.field))

  const send = async () => {
    const body = text.trim()
    if (!body) return
    try {
      await comments.add.mutateAsync(body)
      setText('')
    } catch {
      notifications.show({ color: 'danger', message: t('errors.generic') })
    }
  }

  return (
    <SectionCard title={t('activity.title')}>
      <Stack gap="md">
        <div className={classes.composer}>
          <InitialsAvatar name={me.user.name} size={32} />
          <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
            <Textarea
              autosize
              minRows={1}
              maxRows={8}
              maxLength={MAX_COMMENT_LENGTH}
              placeholder={t('activity.placeholder')}
              aria-label={t('activity.comment')}
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
              onKeyDown={getHotkeyHandler([['mod+Enter', send]])}
            />
            {text.trim() && (
              <Group justify="flex-end">
                <Button size="compact-md" onClick={send} loading={comments.add.isPending}>
                  {t('activity.send')}
                </Button>
              </Group>
            )}
          </Stack>
        </div>

        <ol className={classes.timeline}>
          {shown.map((item) => (
            <li key={`${item.kind}-${item.id}`} className={classes.entry} data-kind={item.kind}>
              {item.kind === 'comment' ? (
                <>
                  <InitialsAvatar name={item.userName} size={32} />
                  <Comment item={item} resource={resource} recordId={recordId} mine={item.userId === me.user.id} locale={locale} />
                </>
              ) : (
                <>
                  <span className={classes.dot} data-action={item.action} />
                  <div className={classes.event}>
                    <Text size="sm">
                      <b>{item.userName}</b>{' '}
                      {item.via && (
                        <Text span inherit c="dimmed">
                          {t('activity.via', { name: item.via })}{' '}
                        </Text>
                      )}
                      {item.summary} {editedAfter(item) ? <StatusPill size="sm" tone="warning" label={t('activity.afterSubmit')} /> : null}
                    </Text>
                    {item.changes?.length ? (
                      <ul className={classes.changes}>
                        {item.changes.map((c) => (
                          <li key={c.field}>{changeText(resource, c, activity?.refs ?? {}, t, locale)}</li>
                        ))}
                      </ul>
                    ) : null}
                    <When at={item.at} locale={locale} />
                  </div>
                </>
              )}
            </li>
          ))}
        </ol>
        {!all && items.length > PAGE && (
          <Button variant="subtle" onClick={() => setAll(true)} style={{ alignSelf: 'flex-start' }}>
            {t('activity.showAll', { count: items.length })}
          </Button>
        )}
      </Stack>
    </SectionCard>
  )
}

/**
 * Next to a submitted document's status: how many fields were changed after it was
 * submitted; opens the list with each field's latest change.
 */
export function AfterSubmitBadge({ resource, activity, locale }: { resource: Resource; activity?: ActivityResponse; locale: Locale }) {
  const { t } = useTranslation()
  const fields = activity?.afterSubmit.fields ?? []
  if (!activity || !fields.length) return null
  const latest = (field: string) =>
    activity.items.find((i) => i.kind === 'audit' && i.afterSubmit && i.changes?.some((c) => c.field === field))
  return (
    <Popover width={360} position="bottom-start" shadow="md">
      <Popover.Target>
        <button type="button" className={classes.badge}>
          {t('activity.editedAfterSubmit', { count: fields.length })}
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="sm" fw={500} mb={8}>
          {t('activity.editedAfterSubmitTitle', { count: fields.length, edits: activity.afterSubmit.edits })}
        </Text>
        <ul className={classes.badgeList}>
          {fields.map((name) => {
            const item = latest(name)
            const change = item?.changes?.find((c) => c.field === name)
            return (
              <li key={name}>
                <Text size="sm">{change ? changeText(resource, change, activity.refs, t, locale) : name}</Text>
                {item && (
                  <Text size="xs" c="dimmed">
                    {item.userName} · {formatDateTime(item.at, { locale })}
                  </Text>
                )}
              </li>
            )
          })}
        </ul>
      </Popover.Dropdown>
    </Popover>
  )
}
